"use client";

import { useId, useRef } from "react";
import { usePathname } from "next/navigation";
import {
  Sun,
  Monitor,
  Clock3,
  SlidersHorizontal,
  Flower2,
  Leaf,
  Snowflake,
  X,
} from "lucide-react";
import { useGardenAtmosphere } from "./GardenAtmosphereProvider";
import {
  formatGardenTime,
  SEASONS,
  SEASON_NAMES,
  type GardenMode,
} from "@/lib/garden-atmosphere";

const seasonIcons = {
  spring: Flower2,
  summer: Sun,
  autumn: Leaf,
  winter: Snowflake,
};

export default function GardenThemeToggle({
  embedded = false,
}: {
  embedded?: boolean;
}) {
  const { ready, settings, setMode, setMinutes, setSeason } =
    useGardenAtmosphere();
  const isEn = (usePathname() || "/").startsWith("/en");
  const id = useId();
  const panel = useRef<HTMLDivElement>(null);
  const modes: { mode: GardenMode; label: string; icon: typeof Sun }[] = [
    { mode: "system", label: "System", icon: Monitor },
    { mode: "auto", label: "Auto", icon: Clock3 },
    {
      mode: "manual",
      label: isEn ? "Manual" : "Manuell",
      icon: SlidersHorizontal,
    },
  ];
  return (
    <div className="garden-controls">
      <div
        role="group"
        aria-label={isEn ? "Ambience" : "Ambiente"}
        className="garden-mode-group"
      >
        {modes.map(({ mode, label, icon: Icon }) => (
          <button
            key={mode}
            type="button"
            disabled={!ready}
            onClick={() => {
              setMode(mode);
              if (!embedded && mode !== "manual") panel.current?.hidePopover();
            }}
            popoverTarget={!embedded && mode === "manual" ? id : undefined}
            aria-label={label}
            aria-pressed={ready && settings.mode === mode}
            title={
              mode === "auto"
                ? isEn
                  ? "Local time and calendar season"
                  : "Lokale Uhrzeit und Jahreszeit nach Kalender"
                : mode === "system"
                  ? isEn
                    ? "Light and dark follow your browser or OS"
                    : "Hell und Dunkel folgen Browser oder Betriebssystem"
                  : label
            }
            className="garden-mode-button"
          >
            <Icon aria-hidden="true" size={14} />
            <span>{label}</span>
          </button>
        ))}
      </div>
      <div
        id={id}
        ref={panel}
        popover={embedded ? undefined : "auto"}
        hidden={embedded && settings.mode !== "manual"}
        className={embedded ? "garden-inline-settings" : "garden-manual-panel"}
        role={embedded ? undefined : "dialog"}
        aria-label={
          isEn ? "Your ambience, your time" : "Dein Ambiente, deine Zeit"
        }
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400">
              {isEn ? "Ambience" : "Ambiente"}
            </p>
            <h2 className="mt-1 text-base font-semibold">
              {isEn ? "Your ambience, your time" : "Dein Ambiente, deine Zeit"}
            </h2>
          </div>
          {!embedded && (
            <button
              type="button"
              popoverTarget={id}
              popoverTargetAction="hide"
              className="garden-close"
              aria-label={isEn ? "Close settings" : "Einstellungen schließen"}
            >
              <X size={16} />
            </button>
          )}
        </div>
        <div className="mt-6">
          <div className="flex items-center justify-between">
            <label htmlFor={`${id}-time`} className="text-sm font-medium">
              {isEn ? "Time of day" : "Tageszeit"}
            </label>
            <output
              htmlFor={`${id}-time`}
              className="font-mono text-sm tabular-nums"
            >
              {formatGardenTime(settings.minutes)}
            </output>
          </div>
          <input
            id={`${id}-time`}
            type="range"
            min="0"
            max="1440"
            step="1"
            value={settings.minutes}
            onChange={(event) => setMinutes(Number(event.target.value))}
            aria-valuetext={`${formatGardenTime(settings.minutes)}${settings.minutes === 1440 ? (isEn ? ", next day" : ", nächster Tag") : ""}`}
            className="garden-time-slider mt-4 w-full"
          />
          <div
            aria-hidden="true"
            className="mt-2 flex justify-between font-mono text-[10px] text-neutral-500 dark:text-neutral-400"
          >
            <span>00:00</span>
            <span>06:00</span>
            <span>12:00</span>
            <span>18:00</span>
            <span>24:00</span>
          </div>
        </div>
        <fieldset className="mt-6">
          <legend className="mb-3 text-sm font-medium">
            {isEn ? "Season" : "Jahreszeit"}
          </legend>
          <div className="grid grid-cols-4 gap-1.5">
            {SEASONS.map((season) => {
              const Icon = seasonIcons[season];
              return (
                <button
                  key={season}
                  type="button"
                  onClick={() => setSeason(season)}
                  aria-pressed={settings.season === season}
                  className="garden-season-button"
                >
                  <Icon size={18} aria-hidden="true" />
                  <span>{SEASON_NAMES[isEn ? "en" : "de"][season]}</span>
                </button>
              );
            })}
          </div>
        </fieldset>
        <p className="mt-5 text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">
          {isEn
            ? "Manual follows your chosen time, Auto your local clock. System follows your browser or OS. Seasons follow the calendar unless chosen manually."
            : "Manuell folgt deiner gewählten Zeit, Auto deiner Ortszeit. System übernimmt Hell/Dunkel vom Browser oder Betriebssystem. Die Jahreszeit folgt dem Kalender, solange du sie nicht manuell wählst."}
        </p>
      </div>
    </div>
  );
}
